import styles from './ObservationModal.module.scss';
import Modal from '../Modal';
import { useModal } from '../../../providers/ModalProvider';
import { modals } from '../../../providers/modals';
import { Text } from '../../typography';
import { Form, Spinner } from 'react-bootstrap';
import UserMultiSelector from '../../UserMultiSelector';
import { useEffect, useMemo, useState, useRef } from 'react';
import { openFileViewer, showToast } from '../../../graphql/cache/modal';
import { toastLength, toastVariant } from '../../../constants/misc';
import { inputTypes } from '../../../constants/strings';
import MultiSelect from '../../forms/MultiSelect';
import { dateOptions } from '../../../utilities/inspection';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../utilities/translator/translation_keys.js';
import moment from 'moment';
import ImageWrapper from '../../offline-wrapper-functions/image-wrapper.js';
import { useObservations } from '../../../hooks/offline-hooks/observationHook.js';
import { useOnlineStatus } from '../../../hooks/offline-hooks/offline-misc.js';
import { useCurrentUser } from '../../../providers/UserProvider.jsx';
import QuickFileDropzone from '../../image_uploads/QuickFileDropzone.jsx';
import findFileIcon, { imageExtensions } from '../../../utilities/files.js';
import { loader } from 'graphql.macro';
import { useMutation } from '@apollo/client';
import { DropdownText } from '../../dropdowns/DropdownText.jsx';
import Icon from '../../Icon.jsx';
import UserSelector from '../../UserSelector.jsx';
import QuickAction from '../../../pages/dashboard/QuickAction.jsx';
import { customColors } from '../../../utilities/misc.js';
import { useWorkspace } from '../../../providers/WorkspaceProvider.jsx';

const updateIncidentMutation = loader('./ObservationModal.incident.graphql');

const types = ['RECOGNITION', 'POSITIVE', 'ACTION'];

export default function ObservationModal() {
  const {
    closeModal,
    updateModal,
    modalState,
    openModal,
    openConfirmationModal,
  } = useModal();
  const { user } = useCurrentUser();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { workspace: currentWorkspace } = useWorkspace();

  const onHide = () => {
    openConfirmationModal({
      title: 'Close Window',
      variant: 'danger',
      buttonText: 'Close',
      description:
        'Are you sure you want to close this window? All changes will be discarded and this action cannot be undone.',
      onSubmit: () => {
        closeModal({ modalName: modals.observation });
        setIsIncident(false);
        setSelectedButton(types[1]);
        update({ user: null });
      },
    });
  };

  const online = useOnlineStatus();
  const { addObservations } = useObservations();
  const [isIncident, setIsIncident] = useState(false);
  const [updateIncident] = useMutation(updateIncidentMutation);

  const update = (variables) =>
    updateModal({
      modalName: modals.observation,
      variables: { ...variables },
    });

  const {
    show,
    dateTime = new Date().toISOString(),
    location,
    participants,
    description,
    anonymous,
    images,
    refetch,
    questions,
    preview,
    incidentId,
    user: selectedUser,
    type,
    workspace,
  } = modalState.observationModal;

  const imagesRef = useRef(images);
  imagesRef.current = images;
  const [selectedButton, setSelectedButton] = useState(
    type === 'RECOGNITION' ? types[0] : types[1],
  );
  useEffect(() => {
    if (type === 'RECOGNITION') {
      setSelectedButton(types[0]);
    }
  }, [type]);

  const allRequiredQuestionsAnswered = useMemo(() => {
    const requiredQuestionIds =
      questions
        ?.filter((question) => question.required)
        .map((question) => question.id) || [];
    return requiredQuestionIds.every((id) => {
      const answer = questions?.find((answer) => answer?.id === id);
      return answer && Boolean(answer.value);
    });
  }, [questions]);

  const submitDisabled = useMemo(() => {
    switch (selectedButton) {
      case types[0]:
        return !dateTime || !description || !selectedUser?.id;
      case types[1]:
        return !dateTime || !description;
      case types[2]:
        return !dateTime || !description || !allRequiredQuestionsAnswered;
      default:
        return true;
    }
  }, [
    selectedUser,
    description,
    dateTime,
    selectedButton,
    allRequiredQuestionsAnswered,
  ]);

  const handleInputChange = (id, value, title, description) => {
    const updatedQuestions = questions?.map((q) =>
      q.id === id ? { ...q, value, title, description } : q,
    );
    update({
      questions: updatedQuestions,
    });
  };

  const isRecognition = useMemo(
    () => selectedButton === types[0],
    [selectedButton],
  );

  return (
    <Modal
      titleImage={'🔎'}
      title={
        preview
          ? t(keys.observations.OBSERVATION_PREVIEW)
          : t(keys.observations.MAKE_AN_OBSERVATION)
      }
      open={show}
      disableClickOutside
      hideSubmit={preview}
      onClose={onHide}
      disableCloseOnSubmit
      hideCancel
      submitDisabled={submitDisabled}
      footer={
        preview ? null : (
          <div className={styles.check} data-cy="observation-anonymous-footer">
            <Icon>no_accounts</Icon>
            <div>
              <Text noMargin weight="semiBold">
                {t(keys.observations.ANONYMOUS_SUBMISSION)}
              </Text>{' '}
              <Text size="xs" noMargin>
                {t(keys.utilities.ANONYMOUS_PROMISE)}
              </Text>
            </div>
            <Form.Check
              type="switch"
              onChange={(e) => update({ anonymous: e.target.checked })}
              checked={anonymous}
              data-cy="observation-anonymous-switch"
            />
          </div>
        )
      }
      onSubmit={() => {
        const formatQuestions = questions?.map((q) => {
          if (!q.value) {
            return { ...q, value: t(keys.assessments.NO_ANSWER_PROVIDED) };
          }
          return q;
        });

        const items = isRecognition ? '' : JSON.stringify(formatQuestions);

        const state = modalState.observationModal;
        if (isRecognition) {
          state.type = 'Worker Recognition';
        }
        const participantIds = participants?.map((u) => u.id);
        const p = isRecognition
          ? anonymous
            ? [...participantIds]
            : [...participantIds, user.id]
          : participantIds;

        addObservations({
          variables: {
            ...state,
            workspaceId:
              workspace?.id || currentWorkspace?.id || user.lastWorkspaceId,
            participants: p,
            items,
            recognition: isRecognition,
            userId: isRecognition ? selectedUser?.id : null,
            private: !isRecognition,
          },
        })
          .then((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid response structure');
            }

            const { data, errors } = response;

            if (errors) {
              showToast({
                title: 'Error',
                message: errors[0].message,
                variant: toastVariant.danger,
                time: toastLength.md,
              });
              return;
            }
            const { id } = data.addObservation || {};
            if (id) {
              showToast({
                title: t(keys.observations.OBSERVATION_SUBMITTED),
                message: t(keys.observations.OBSERVATION_SUBMITTED_MESSAGE),
                variant: toastVariant.info,
                time: toastLength.md,
              });
              if (online) {
                refetch();
              }
            }
            if (incidentId) {
              updateIncident({ variables: { incidentId, observationId: id } });
            }
            if (isIncident) {
              openModal({
                modalName: modals.createIncident,
                variables: {
                  title: `Observation ${id}`,
                  participants: participants
                    .map(({ id }) => id)
                    .includes(user.id)
                    ? [...participants]
                    : [...participants, user],
                  observation: data.addObservation,
                  type: 'Health and Safety',
                  subtype: 'Near Miss',
                  description,
                },
              });
            } else {
              showToast({
                title: t(keys.observations.OBSERVATION_SUBMITTED),
                message: t(keys.observations.OBSERVATION_SUBMITTED_MESSAGE),
                variant: toastVariant.info,
                time: toastLength.md,
              });
              refetch();
            }
          })
          .catch((error) => {
            showToast({
              title: t(keys.observations.SUBMIT_ERROR),
              message: t(keys.observations.SUBMIT_ERROR_MESSAGE, {
                variable: error.message || error,
              }),
              variant: toastVariant.danger,
              time: toastLength.md,
            });
          })
          .finally(() => {
            closeModal({ modalName: modals.observation });
            setSelectedButton(types[0]);
          });
      }}
    >
      <div className={styles.container}>
        <div>
          <Text noMargin weight="semiBold" textAlign="center" color="secondary">
            {t(keys.observations.SELECT_OBSERVATION_TYPE)}
          </Text>
          <div className={styles.topButtons}>
            <QuickAction
              textSize="sm-md"
              title={t(keys.observations.POSITIVE_OBSERVATION)}
              icon="mood"
              className={styles.quickAction}
              onClick={() => {
                setSelectedButton(types[1]);
                update({ negative: false, type: null, recognition: false });
              }}
              backgroundColor={
                selectedButton === 'POSITIVE'
                  ? customColors.GREEN
                  : customColors.SECONDARY
              }
            />
            <QuickAction
              textSize="sm-md"
              title={t(keys.observations.ACTION_REQUIRED)}
              icon="flag"
              className={styles.quickAction}
              backgroundColor={
                selectedButton === 'ACTION'
                  ? customColors.RED
                  : customColors.SECONDARY
              }
              onClick={() => {
                setSelectedButton(types[2]);
                update({ negative: true, type: null, recognition: false });
              }}
            />
            <QuickAction
              icon="hotel_class"
              title={t(keys.observations.WORKER_RECOGNITION)}
              textSize="sm-md"
              className={styles.quickAction}
              backgroundColor={
                selectedButton === 'RECOGNITION'
                  ? customColors.GREEN
                  : customColors.SECONDARY
              }
              onClick={() => {
                setSelectedButton(types[0]);
                update({
                  negative: false,
                  recognition: true,
                  type: 'Worker Recognition',
                });
              }}
            />
          </div>
        </div>
        {selectedButton !== types[0] && (
          <div>
            <Text noMargin weight="semiBold">
              {t(keys.observations.SELECT_DATE_AND_TIME)}
              <span className={styles.red}>*</span>
            </Text>
            <input
              type="datetime-local"
              onChange={(e) => {
                const dateTime = e.target.value;
                update({ dateTime });
              }}
              className={styles.dateSelector}
              value={dateTime}
              data-cy="observation-date-time"
            />
          </div>
        )}
        <div>
          <Text noMargin weight="semiBold" size="md">
            {t(keys.action.ENTER, { variable: t(keys.common.LOCATION) })}
            <span className={styles.red}>*</span>
          </Text>
          {!!workspace?.locations ? (
            <DropdownText
              noMargin
              placeholder={'Location'}
              selected={location}
              items={workspace.locations.split('|')}
              onChange={(location) => update({ location })}
              onRemove={() => update({ location: null })}
            />
          ) : (
            <Form.Control
              value={location || undefined}
              onChange={(e) => update({ location: e.target.value })}
              data-cy="observation-location"
            />
          )}
        </div>
        {selectedButton === types[0] && (
          <div>
            <Text noMargin weight="semiBold" size="md">
              {t(keys.observations.RECOGNIZE_COLLEAGUE)}
              <span className={styles.red}>*</span>
            </Text>
            <UserSelector
              cardProps={{ largeAvatar: true, weight: 'regular' }}
              onSelectionChanged={(user) => update({ user })}
              selected={selectedUser}
              onClose={() => update({ user: null })}
              type={'all'}
              usersToLeaveOut={[user]}
            />
          </div>
        )}
        <div>
          <Text noMargin weight="semiBold" size="md">
            {selectedButton === types[0]
              ? t(keys.observations.COLLEAGUE_IMPACT)
              : t(keys.action.ENTER, {
                  variable: t(keys.observations.BRIEF_DESCRIPTION),
                })}
            <span className={styles.red}>*</span>
          </Text>
          <Form.Control
            as="textarea"
            rows={3}
            onChange={(e) => update({ description: e.target.value })}
            value={description}
            data-cy="observation-description"
          />
        </div>
        {selectedButton === types[2] &&
          questions?.map(
            ({
              title,
              description,
              inputType,
              required,
              additionalData,
              id,
            }) => {
              const dateFormat = dateOptions[inputType];
              const input = () => {
                switch (inputType) {
                  case inputTypes.textMulti:
                    return (
                      <Form.Control
                        key={id}
                        as={'textarea'}
                        onChange={(e) =>
                          handleInputChange(
                            id,
                            e.target.value,
                            title,
                            description,
                          )
                        }
                        data-cy={`observation-question-${id}`}
                      />
                    );
                  case inputTypes.select:
                    return (
                      <div data-cy={`observation-question-${id}`}>
                        {additionalData?.split('|').map((a) => (
                          <Form.Check
                            key={a}
                            type="radio"
                            label={a}
                            inline
                            name={`singleselect-${id}`}
                            value={a}
                            onChange={(e) => {
                              handleInputChange(
                                id,
                                e.target.value,
                                title,
                                description,
                              );
                            }}
                          />
                        ))}
                      </div>
                    );
                  case inputTypes.multiSelect:
                    const values = questions?.find((q) => q.id === id)?.value;
                    return (
                      <MultiSelect
                        key={id}
                        title={' '}
                        inline
                        name={`multiselect-${id}`}
                        values={values || []}
                        options={additionalData?.split('|') || []}
                        onChange={(newValue) =>
                          handleInputChange(id, newValue, title, description)
                        }
                        data-cy={`observation-question-${id}`}
                      />
                    );
                  case inputTypes.time:
                  case inputTypes.date:
                  case inputTypes.dateTime:
                    const type = () => {
                      switch (inputType) {
                        case inputTypes.time:
                          return 'time';
                        case inputTypes.dateTime:
                          return 'datetime-local';
                        default:
                          return 'date';
                      }
                    };
                    return (
                      <div key={id} data-cy={`observation-question-${id}`}>
                        <input
                          label={`${t(keys.action.SELECT_VARIABLE, {
                            variable: t(keys.common.DATE),
                          })} ${
                            inputType === inputTypes.date
                              ? t(keys.templates.TIME)
                              : ''
                          }`}
                          type={type()}
                          placeholder="Select Date"
                          className={styles.dateSelector}
                          id={`datetime-${id}`}
                          name={`datetime-${id}`}
                          onChange={(e) => {
                            let value = e.target.value;
                            if (inputType !== inputTypes.time)
                              value = moment(e.target.value).format(
                                dateFormat.format,
                              );
                            handleInputChange(id, value, title, description);
                          }}
                          data-cy={`observation-question-${id}`}
                        ></input>
                      </div>
                    );
                  default:
                    return (
                      <Form.Control
                        key={id}
                        onChange={(e) =>
                          handleInputChange(
                            id,
                            e.target.value,
                            title,
                            description,
                          )
                        }
                        data-cy={`observation-question-${id}`}
                      />
                    );
                }
              };
              return (
                <div key={id}>
                  <div>
                    <Text noMargin weight="semiBold" size="md">
                      {title}{' '}
                      <span className={styles.red}>{required ? '*' : ''}</span>
                    </Text>
                    <Text noMargin size="sm" color="secondary">
                      {description}
                    </Text>
                  </div>
                  {input()}
                </div>
              );
            },
          )}
        {selectedButton !== types[0] && (
          <div>
            <Text noMargin weight="semiBold" size="md">
              {t(keys.observations.SELECT_TEAM)}
            </Text>
            <div className={styles.team}>
              <UserMultiSelector
                stateUpdateOnly
                setUsers={(participants) => update({ participants })}
                onUserAdded={(user) => {
                  update({ participants: [...participants, user] });
                }}
                className={styles.team}
                selected={participants}
                onUserRemoved={(user) => {
                  update({
                    participants: participants?.filter((u) => u.id !== user.id),
                  });
                }}
                data-cy="observation-participants"
              />
            </div>
          </div>
        )}
        {loading ? (
          <Spinner
            className={styles.spinner}
            animation="border"
            variant="primary"
            data-cy="observation-loading-spinner"
          />
        ) : (
          <>
            <QuickFileDropzone
              allowMulti
              onSubmit={({ url }) => {
                const next = [...imagesRef.current, url];
                imagesRef.current = next;
                update({ images: next });
              }}
              setLoading={setLoading}
              icon={!!images.length}
              loading={loading}
              iconText={t(keys.action.ADD, { variable: t(keys.common.FILE) })}
              data-cy="observation-file-dropzone"
            />
            <div className={styles.images}>
              {images.map((image, index) => {
                const extension = image.split('.').pop().toLowerCase();
                const isImage = imageExtensions.includes(extension);
                const src = isImage ? image : findFileIcon(extension);
                const file = {
                  fileType: extension,
                  url: image,
                  imageUrl: image,
                  downloadAllowed: true,
                };
                return (
                  <div
                    key={`image-${image}-${index}`}
                    data-cy={`observation-image-${index}`}
                  >
                    <ImageWrapper
                      key={image.id}
                      alt={`observation-${index}`}
                      style={{ height: '8rem', width: 'auto' }}
                      image={src}
                      className={isImage ? styles.image : styles.file}
                      onClick={() => openFileViewer(file)}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
